import axios from 'axios'
import { auth } from './auth'
import router from './router'
import Vue from 'vue'

const apiUrl = process.env.API

// Api routes
const apiRoutes = {
  base: (url) => {
    return `${apiUrl}${url}`
  },
  transactions: {
    users: {
      purchases(id) {
        return `transactions/users/${id}/purchases`
      },
      deposits(id) {
        return `transactions/users/${id}/deposits`
      }
    }
  },
  invoices: {
    get (id) {
      return `invoices/${id}`
    },
    index (id, month) {
      return `invoices?organizationId=${id}&month=${month}`
    },
    create: 'invoices'
  },
  payments: {
    charges: 'payments/charges',
    cards: {
      save (token) {
        return `payments/cards?token=${token}`
      },
      remove (id) {
        return `payments/cards/${id}`
      },
      get: 'payments/cards'
    },
    autoRefill: 'payments/auto'
  },
  auth: {
    signIn: 'auth/signin',
    signUp: 'auth/signup',
    forgot: 'auth/forgot',
    reset(id) {
      return `auth/reset/${id}`
    }
  },
  gasTypes: {
    index: 'gas/types'
  },
  stations: {
    index: 'stations',
    create: 'stations',
    qr (id) {
      return `stations/${id}/qr`
    },
    update (id) {
      return `stations/${id}`
    },
    delete (id) {
      return `stations/${id}`
    }
  },
  station (id) {
    return {
      get: `stations/${id}`,
      transactions: `stations/${id}/transactions`,
      update: `stations/${id}`,
      connect: `stations/${id}/connect`,
      disconnect: `stations/${id}/disconnect`,
      certifications: `stations/${id}/certifications`,
      certification (cid) {
        const url = `stations/${id}/certifications/${cid}`
        return { get: url, update: url, remove: url }
      }
    }
  },
  countries: {
    index: 'countries',
    add: 'countries',
    update (id) {
      return `countries/${id}`
    },
    delete (id) {
      return `countries/${id}`
    }
  },
  currencies: {
    index: 'currencies',
    add: 'currencies',
    update (id) {
      return `currencies/${id}`
    },
    delete (id) {
      return `currencies/${id}`
    }
  },
  country (id = null) {
    return {
      index: 'countries'
    }
  },
  user (id) {
    return {
      get: `users/${id}`,
      transactions: `users/${id}/transactions`,
      organizations: `users/${id}/organizations`,
      stations: `users/${id}/stations`,
      update: `users/${id}`,
      nfcTags: {
        index: `users/${id}/nfc-tags`,
        one (tagId) {
          return `users/${id}/nfc-tags/${tagId}`
        }
      }
    }
  },
  users: {
    index: 'users',
    create: 'users',
    get (id) {
      return `users/${id}`
    },
    update (id) {
      return `users/${id}`
    },
    saldo: 'users/saldo',
    transactions (id) {
      return `users/${id}/transactions`
    }
  },
  organizations: {
    index: 'organizations',
    update (id) {
      return `organizations/${id}`
    },
    get (id) {
      return `organizations/${id}`
    },
    billings: 'organizations/billings',
    users: {
      index: 'organizations/users',
      get (oid, uid) {
        return `organizations/${oid}/users/${uid}`
      },
      update (oid, uid, query) {
        return `organizations/${oid}/users/${uid}${(query ? '?responseFlag=true' : '')}`
      },
      delete (oid, uid) {
        return `organizations/${oid}/users/${uid}`
      },
      create (oid) {
        return `organizations/${oid}/users`
      },
      transactions (orgId, id) {
        return `organizations/${orgId}/users/${id}/transactions`
      }
    },
    create: 'organizations',
    update (id) {
      return `organizations/${id}`
    },
    user (id) {
      return {
        create: `organizations/${id}/`
      }
    }
  },
  files: 'files',
  organization (id) {
    return {
      get: `organizations/${id}`,
      update: `organizations/${id}`,
      stations: `organizations/${id}/stations`,
      createStation: `organizations/${id}/stations?uid=${auth.user.id}`,
      gasTypes: `organizations/${id}/gas/types`,
      users: {
        index: `organizations/${id}/users`,
        create: `organizations/${id}/users`
      },
      removeUser (ouid) {
        return `organizations/${id}/users/${ouid}?uid=${auth.user.id}`
      },
      user (uid) {
        return {
          transactions: `organizations/${id}/users/${uid}/transactions`,
          create: `organizations/${id}/users`,
          remove: `organizations/${id}/users/${uid}`,
          update: `organizations/${id}/users/${uid}`,
          inviteResponse (res) {
            return `organizations/${id}/users/${uid}/invite?answer=${res}`
          },
          requestResponse (res) {
            return `organizations/${id}/users/${uid}/request?answer=${res}`
          },
          toggleAdmin (res) {
            return `organizations/${id}/users/${uid}/admin?state=${res}`
          }
        }
      },
      gasPrice (pid) {
        return {
          create: `organizations/${id}/prices`,
          update: `organizations/${id}/prices/${pid}`,
          remove: `organizations/${id}/prices/${pid}`
        }
      },
      resolvedPricesForStation (stationId) {
        return `organizations/${id}/prices/station/${stationId}`
      },
      groups: `organizations/${id}/groups`,
      group (gid) {
        return {
          get: `organizations/${id}/groups/${gid}`,
          update: `organizations/${id}/groups/${gid}`,
          remove: `organizations/${id}/groups/${gid}`,
          user (uid) {
            const url = `organizations/${id}/groups/${gid}/users/${uid}`
            return { add: url, remove: url }
          }
        }
      }
    }
  }
}

// Create axios instance with base urls and auth headers
let api = axios.create({
  baseURL: apiUrl
})

api.interceptors.request.use(config => {
  const token = auth.getToken()

  if (token != null) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.response.use(response => {
  return response
}, error => {
  if (error.response && error.response.status === 401) {
    auth.logout()
    router.push('/signin?redirect=' + window.location.pathname)
    Vue.prototype.$message.error('Please sign in again!')
  }
  return Promise.reject(error)
})

export {
  apiUrl,
  apiRoutes,
  api
}



// WEBPACK FOOTER //
// ./src/api.js